/** Runtime API bases always use same-origin BFF routes. */
export const trussClientBase = '/api/truss';
export const trussSearchClientBase = '/api/truss';

/** Public Truss API URL for docs / curl samples (ApiCodeSamples); not the runtime base when BFF is on */
export const apiUrl = import.meta.env.VITE_API_URL || '';
export const apiSearchUrl = import.meta.env.VITE_API_SEARCH_URL || apiUrl;

/**
 * Base URL for an endpoint. Use for /search and /product/search; otherwise returns apiUrl.
 */
export const getApiUrl = (endpoint: string): string =>
  endpoint.includes('/search') ? apiSearchUrl : apiUrl;

export const MAX_DATE_RANGE = 6;

// Relative date range presets
export const RELATIVE_DATE_PRESETS = [
  { value: 1, unit: 'hours' as const, label: 'Last 1 hour' },
  { value: 6, unit: 'hours' as const, label: 'Last 6 hours' },
  { value: 12, unit: 'hours' as const, label: 'Last 12 hours' },
  { value: 24, unit: 'hours' as const, label: 'Last 24 hours' },
  { value: 3, unit: 'days' as const, label: 'Last 3 days' },
  { value: 7, unit: 'days' as const, label: 'Last 7 days' },
] as const;

export const SCALAR_ATTRIBUTES = ['category', 'source'] as const;
export const ARRAY_ATTRIBUTES = ['author', 'industry', 'region', 'reference', 'tags'] as const;
export const INDICATOR_ATTRIBUTES = ['indicators'] as const;
export const PRODUCT_ATTRIBUTES = [...SCALAR_ATTRIBUTES, ...ARRAY_ATTRIBUTES, ...INDICATOR_ATTRIBUTES] as const;

export const regionNameMap: Record<string, string> = {
  "United States": "United States of America",
  "Hong Kong": "Hong Kong S.A.R.",
  "Ivory Coast": "Ivory Coast",
  "Russia": "Russia", // Updated to match GeoJSON name
  "Syria": "Syria",
  "Iran": "Iran",
  "Czech Republic": "Czech Republic",
  "Moldova": "Moldova",
  "South Korea": "South Korea",
  "North Korea": "North Korea",
  "Vietnam": "Vietnam",
  "Bolivia": "Bolivia",
  "Venezuela": "Venezuela",
  "United Kingdom": "United Kingdom",
  "Democratic Republic of the Congo": "Democratic Republic of the Congo",
  "Republic of Congo": "Republic of Congo",
  "Myanmar": "Myanmar",
  "Palestine": "Palestine",
  "Laos": "Laos",
  "Tanzania": "United Republic of Tanzania",
  "Swaziland": "Swaziland",
  "Cape Verde": "Cape Verde",
  "Brunei": "Brunei",
  "Macau": "Macao S.A.R",
  "East Timor": "East Timor",
  "Micronesia": "Federated States of Micronesia",
  // Additional mappings based on the GeoJSON country list
  "Federated States of Micronesia": "Federated States of Micronesia",
  "Saint Kitts and Nevis": "Saint Kitts and Nevis",
  "Saint Vincent and the Grenadines": "Saint Vincent and the Grenadines",
  "Saint Lucia": "Saint Lucia",
  "Antigua and Barbuda": "Antigua and Barbuda",
  "Bosnia and Herzegovina": "Bosnia and Herzegovina",
  "North Macedonia": "North Macedonia",
  "Central African Republic": "Central African Republic",
  "Equatorial Guinea": "Equatorial Guinea",
  "Trinidad and Tobago": "Trinidad and Tobago",
  "Papua New Guinea": "Papua New Guinea",
  //... continue mapping as needed
};

/** Shared customer API usage plan (Access + Deployment). Community keys use plan `po0bdp` and stay disabled until upgrade. */
export const SHARED_CUSTOMER_USAGE_PLAN_ID = 'ycbyvm' as const;

export const USER_TYPES = {
  COMMUNITY: {
    planId: 'po0bdp',
    accessType: 'search',
    role: 'community'
  },
  ACCESS: {
    planId: SHARED_CUSTOMER_USAGE_PLAN_ID,
    accessType: 'search',
    role: 'access'
  },
  DEPLOYMENT: {
    planId: SHARED_CUSTOMER_USAGE_PLAN_ID,
    accessType: 'search',
    role: 'deployment'
  },
  /** @deprecated Use ACCESS — kept for callers still using GROWTH. */
  GROWTH: {
    planId: SHARED_CUSTOMER_USAGE_PLAN_ID,
    accessType: 'search',
    role: 'access'
  },
  /** @deprecated Use DEPLOYMENT — kept for callers still using SCALE. */
  SCALE: {
    planId: SHARED_CUSTOMER_USAGE_PLAN_ID,
    accessType: 'search',
    role: 'deployment'
  },
  TEST: {
    planId: 'gxhpyq',
    accessType: 'search',
    role: 'test'
  },
  TRUSS: {
    planId: 'a0eqgv',
    accessType: 'admin',
    role: 'truss'
  }
} as const;

export type UsagePlanId = (typeof USER_TYPES)[keyof typeof USER_TYPES]['planId'];
export type AccessType = (typeof USER_TYPES)[keyof typeof USER_TYPES]['accessType'];
export type RoleType = (typeof USER_TYPES)[keyof typeof USER_TYPES]['role'];